import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { trackEvent } from '../config/analytics';

const FUNCTIONS_BASE_URL = 'https://us-central1-accessshield-ai.cloudfunctions.net';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUserData, userData } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyAndUpdateSubscription = async () => {
      if (!sessionId) {
        setStatus('error');
        setError('No session ID found. Please contact support.');
        return;
      }

      try {
        // Get the user's ID token for authentication
        const idToken = await auth.currentUser?.getIdToken();

        if (!idToken) {
          setStatus('error');
          setError('Not logged in. Please log in and try again.');
          return;
        }

        // Call our verify endpoint to confirm payment and update subscription
        const response = await fetch(`${FUNCTIONS_BASE_URL}/verifyPaymentSession`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ sessionId })
        });

        const data = await response.json();

        if (response.ok && data.result?.success) {
          // Payment verified and subscription updated
          trackEvent('subscription_activated', {
            tier: data.result.tier,
            sessionId
          });

          // Refresh user data to get updated subscription status
          await refreshUserData();
          setStatus('success');
        } else {
          // Verification failed, but let's check if subscription was already updated by webhook
          await refreshUserData();

          // Check if user now has active subscription
          if (userData?.subscriptionStatus === 'active') {
            setStatus('success');
          } else {
            throw new Error(data.error || 'Payment verification failed');
          }
        }
      } catch (err) {
        console.error('Payment verification error:', err);

        // Try refreshing user data - maybe webhook already processed it
        try {
          await refreshUserData();
          // Check if subscription is now active
          const freshData = await refreshUserData();
          if (freshData?.subscriptionStatus === 'active') {
            setStatus('success');
            return;
          }
        } catch (refreshErr) {
          console.error('Refresh error:', refreshErr);
        }

        // If we've tried a few times, show error but with support option
        if (retryCount >= 2) {
          setStatus('error');
          setError('We received your payment but are having trouble activating your account. Please contact support or try refreshing in a few minutes.');
        } else {
          // Retry after a delay (webhook might still be processing)
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            verifyAndUpdateSubscription();
          }, 3000);
        }
      }
    };

    verifyAndUpdateSubscription();
  }, [sessionId, retryCount]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleRetry = () => {
    setStatus('verifying');
    setRetryCount(0);
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <LoadingSpinner size="lg" />
          <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-2">
            Activating Your Subscription
          </h1>
          <p className="text-gray-600">
            Please wait while we confirm your payment and activate your account...
          </p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-4">
              Still processing... (attempt {retryCount + 1})
            </p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Almost There!
          </h1>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <div className="space-y-3">
            <Button fullWidth onClick={handleRetry}>
              Try Again
            </Button>
            <Button fullWidth variant="secondary" onClick={handleGoToDashboard}>
              Go to Dashboard
            </Button>
            <p className="text-sm text-gray-500">
              Need help? Email us at{' '}
              <a href="mailto:support@accessshield.ai" className="text-indigo-600 hover:underline">
                support@accessshield.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-600 mb-6">
          Your subscription is now active. You have full access to all features included in your plan.
        </p>
        <Button fullWidth onClick={handleGoToDashboard}>
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
